import { Link } from 'react-router-dom';
import Button from '@/components/Button.tsx';
import { TypoCallout } from '@/components/Typography.tsx';
import { ArrowLeft, ArrowRight, Close, InfoCircle } from '@/components/Icons.tsx';
import { ReactNode, useState } from 'react';
import Popover from '@/components/Popover.tsx';
import useHorizontalRow from '@/hooks/useHorizontalRow.ts';

const Pricing = () => {
  const { rowRef, isScrollable, isAtStart, isAtEnd, scrollPrev, scrollNext } = useHorizontalRow();

  return (
    <section id="pricing-section" className="bg-common-white text-[#151012] pt-24 pb-14 md:pb-24 text-center px-4">
      <h3 className="font-primary text-title md:text-heroTitle !font-extraBold">
        Choose what's right for you
      </h3>

      {/* The controls are a sibling of the centred table, not another flex item inside it:
          as a third item they would push the header column off centre. Hence the wrapper,
          which carries the vertical rhythm the table used to carry itself. */}
      <div className="mt-24 mb-10 md:mb-16">
        <div className="flex flex-shrink-0 flex-nowrap justify-center">
          <div
            className="flex flex-col flex-shrink-0 items-start sm:w-[260px]
            border-solid border-r border-[#00000014]"
          >
            <TableHeader title="&nbsp;" price="&nbsp;" />
            {
              PRODUCT_HEADERS.map((header, index) =>
                <TableRowHeader key={index} title={header.title} description={header.description} />)
            }
            <div className="flex items-center flex-shrink-0 self-stretch min-h-0 h-16 pr-2 md:pr-6 text-left">
              <strong
                className="text-[#151012] text-callout sm:text-body
                !font-semibold leading-normal flex-1 flex-shrink-0 -mb-12"
              >
                Private tutoring <br />
                with Mark Anestis<br />
                (live via zoom)
              </strong>

              <InfoTooltip>We will schedule these sessions at a time that works for you!</InfoTooltip>
            </div>
            <div
              className="hidden sm:flex justify-end items-center flex-shrink-0 self-stretch
              min-h-0 h-16 pr-2 md:pr-6 text-right"
            >
              <em className="text-footnote text-[#00000080]">Optional</em>
            </div>
          </div>
          <div className="flex overflow-y-hidden overflow-x-auto" ref={rowRef}>
            {
              PRODUCTS.map((product, index) =>
                <TableColumn product={product} key={index} first={index === 0} />)
            }
            {/* On narrow phones, add just enough scroll range for the last plan to occupy the
                same position as every preceding plan. Wider layouts already expose multiple
                plans, so forcing that alignment there would create a large empty tail. */}
            <div
              aria-hidden="true"
              className="hidden m:block m:flex-[0_0_max(0px,calc(100%-13.875rem))]"
            />
          </div>
        </div>

        {
          // Nothing to page => no controls at all, same rule as the video-tutorial rows. The
          // four columns fit from 1280 up, so no chevrons render on a desktop.
          //
          // One class differs from the video-tutorial control: the disabled colour. The
          // `shade-*` tokens are white alphas (`--shade-30: #FFFFFF4C`) and this section is
          // `bg-common-white`, so `disabled:text-shade-30` renders an invisible chevron here --
          // which reads as "there is no control to the left" rather than "there is nothing
          // further left". `neutral-text-weak` is the palette's disabled-text colour.
          isScrollable && (
            <div className="flex justify-center mt-6">
              <button
                className="bg-green-500 text-primary p-2 disabled:text-neutral-text-weak"
                aria-label="Previous plan"
                disabled={isAtStart}
                onClick={scrollPrev}>
                <ArrowLeft width={24} height={24} />
              </button>
              <button
                className="bg-green-500 text-primary p-2 disabled:text-neutral-text-weak"
                aria-label="Next plan"
                disabled={isAtEnd}
                onClick={scrollNext}>
                <ArrowRight width={24} height={24} />
              </button>
            </div>
          )
        }
      </div>

      <Link to="/sign-in" className="m:inline-flex">
        <Button className="px-3 md:px-6 py-2 md:py-5 rounded-full m:h-11" type="button">GET STARTED NOW</Button>
      </Link>
    </section>
  );
};

const InfoTooltip = ({ children }: { children: ReactNode }) => {
  const [visible, setVisible] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>();

  return (
    <>
      <button
        onClick={(event) => {
          setVisible(true);
          setAnchor(event.currentTarget);
        }}
        className={`hidden sm:flex 
          items-center justify-center p-1 w-8 h-8 text-center text-heading 
          font-bold border border-shade-10 m:w-11 m:h-11
        `}
      ><InfoCircle className="text-[#00000080]" /></button>
      <Popover
        width="17.25rem"
        anchor={anchor}
        title=""
        open={visible}
        onClose={() => setVisible(false)}
        placement="right-start"
        className="!p-6"
        alt
      >
        <p className="text-callout text-common-black leading-normal">
          {children}
        </p>
      </Popover>
    </>
  );
};

const TableHeader = ({ title = '', price = '' }: Partial<ProductProps>) => (
  <div className="flex flex-col items-stretch">
    <strong className="text-heroSubtitle font-extraBold text-primary leading-6">{title}</strong>
    <em className="text-callout italic leading-normal">{price}</em>
  </div>
);

const TableRowHeader = ({ title, description }: { title: string; description: string; }) => (
  <div className="flex items-center flex-shrink-0 self-stretch min-h-0 h-16 pr-2 md:pr-6 text-left">
    <strong className="text-[#151012] text-callout sm:text-body !font-semibold leading-normal flex-1 flex-shrink-0">
      {title}
    </strong>
    <InfoTooltip>{description}</InfoTooltip>
  </div>
);

const TableCell = ({ text, className = '' }: { text: string | null; className?: string }) => (
  <div className="flex justify-center items-center flex-shrink-0 self-stretch min-h-0 h-16 px-5">
    {text ? <TypoCallout className={`text-[#151012] font-medium ${className}`}>{text}</TypoCallout> : <Close />}
  </div>
);

const TableColumn = ({ product, first = false }: { product: ProductProps; first?: boolean }) => (
  <div
    className={`flex flex-col flex-shrink-0 items-stretch min-w-0 w-[13.875rem]
    border-solid ${first ? 'border-none' : 'border-l'} border-[#00000014]`}
  >
    <TableHeader title={product.title} price={product.price} />
    <TableCell text={product.tests} />
    <TableCell text={product.packs} className="max-w-28" />
    <TableCell text={product.studyCenter} />
    <TableCell text={product.moduleMode} />
    <TableCell text={product.videoTutorials} />
    <TableCell text={product.privateAccess} />
    <TableCell text={product.description} />
  </div>
);

interface ProductProps {
  title: string;
  price: string;
  tests: string;
  packs: string;
  studyCenter: string | null;
  moduleMode: string | null;
  videoTutorials: string | null;
  privateAccess: string | null;
  description: string;
}

const PRODUCT_HEADERS = [
  {
    title: 'Practice Tests',
    // eslint-disable-next-line max-len
    description: 'There are currently 9 tests available. Idiot Tutor Mark Anestis regularly creates and adds new tests to the SATPrac site to best mimic what is showing up on recent digital SATs.',
  },
  {
    title: 'SATPrac Packs',
    // eslint-disable-next-line max-len
    description: 'These are 8-question sets that focus on specific question types. ~2,000 practice questions as well as access to super-challenging packs that will get you ready for the hardest questions you may encounter.',
  },
  {
    title: 'Study Center',
    // eslint-disable-next-line max-len
    description: 'This tool is designed to help you organize your study plan and keep tabs on which question types you need to work on more. It will help you project what you can expect to score on the actual digital SAT based on your understanding of the key fundamentals.',
  },
  {
    title: 'Module Mode',
    // eslint-disable-next-line max-len
    description: 'Module Mode allows you to attack individual digital SAT modules. (22 math questions in 35 minutes or 27 reading and writing questions in 32 minutes). Use this mode to work on your timing skills!',
  },
  {
    title: 'Video Tutorials',
    // eslint-disable-next-line max-len
    description: 'The 100+ hours of videos include: question type tutorials, question bank tutorials, test explanation tutorials, strategy tutorials, and studying tutorials',
  },
];

const PRODUCTS = [
  {
    title: 'Lite',
    price: 'Free',
    tests: '1',
    packs: '1 of each question type',
    studyCenter: null,
    moduleMode: null,
    videoTutorials: null,
    privateAccess: null,
    description: '$375/hr for a private session'
  },
  {
    title: 'Starter',
    price: '$199',
    tests: '4',
    packs: '3 of each question type',
    studyCenter: null,
    moduleMode: null,
    videoTutorials: null,
    privateAccess: null,
    description: '1 bundle of 3 sessions for $825. Additional sessions available at $350/hr.'
  },
  {
    title: 'Complete',
    price: '$699',
    tests: 'All available',
    packs: 'All available',
    studyCenter: 'Full Access',
    moduleMode: 'Full Access',
    videoTutorials: 'Full Access',
    privateAccess: null,
    description: '1 bundle of 3 sessions for $600. Additional sessions available at $325/hr'
  },
  {
    title: 'Concierge',
    price: '$1,199',
    tests: 'All available',
    packs: 'All available',
    studyCenter: 'Full Access',
    moduleMode: 'Full Access',
    videoTutorials: 'Full Access',
    privateAccess: '3 sessions included',
    description: 'Additional sessions available at $325/hr'
  },
];

export default Pricing;
